import {
  AIRGAP_READY_TIMEOUT_MS,
  CONSENT_INTERACTION_EVENT_TYPES,
  CONSENT_MANAGER_ELEMENT_ID,
} from '@/constants';

type InteractionEvent = DocumentEventMap[(typeof CONSENT_INTERACTION_EVENT_TYPES)[number]];

const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

type LoadedAirgap = NonNullable<Window['airgap']>;

/**
 * Resolves the loaded Airgap API, or null when Transcend is not configured or never finished
 * loading. index.html stubs window.airgap, so ready() queues until the async script runs.
 */
export const whenAirgapReady = (): Promise<LoadedAirgap | null> => {
  const airgap = window.airgap;
  if (!airgap) return Promise.resolve(null);

  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), AIRGAP_READY_TIMEOUT_MS);
    const settle = (value: LoadedAirgap | null) => {
      window.clearTimeout(timeout);
      resolve(value);
    };

    try {
      // The callback gets the loaded API; the stub captured above never gains getConsent.
      airgap.ready((api) => settle(typeof api.getConsent === 'function' ? api : null));
    } catch {
      settle(null);
    }
  });
};

const isFromConsentManager = (event: Event): boolean => {
  const manager = document.getElementById(CONSENT_MANAGER_ELEMENT_ID);

  return !!manager && event.target instanceof Node && manager.contains(event.target);
};

export type InteractionEventSource = {
  // Resolves null once released, so a waiting caller can finish.
  next: () => Promise<InteractionEvent | null>;
  abort: () => void;
  stop: () => void;
};

/**
 * Hands out user interactions one at a time, as proof for setConsent. Airgap accepts an
 * interaction long after it fired, so one held from before the database read still counts.
 */
export const createInteractionEventSource = (): InteractionEventSource => {
  let pending: InteractionEvent | null = null;
  let aborted = false;
  let resolveNext: ((event: InteractionEvent | null) => void) | null = null;

  const release = () => {
    aborted = true;
    const resolve = resolveNext;
    resolveNext = null;
    if (resolve) resolve(null);
  };

  const handle = (event: InteractionEvent) => {
    // A banner click is the user answering; restoring on it would overwrite their choice.
    if (isFromConsentManager(event)) return;

    if (!resolveNext) {
      pending = event;
      return;
    }

    const resolve = resolveNext;
    resolveNext = null;
    resolve(event);
  };

  CONSENT_INTERACTION_EVENT_TYPES.forEach((type) =>
    document.addEventListener(type, handle, LISTENER_OPTIONS)
  );

  return {
    // Each interaction is handed out once, so a retry waits for a new one.
    next: () => {
      // Latched: the abort can land before the caller comes back to wait.
      if (aborted) return Promise.resolve(null);

      if (pending) {
        const event = pending;
        pending = null;

        return Promise.resolve(event);
      }

      return new Promise<InteractionEvent | null>((resolve) => {
        resolveNext = resolve;
      });
    },
    abort: release,
    stop: () => {
      release();
      CONSENT_INTERACTION_EVENT_TYPES.forEach((type) =>
        document.removeEventListener(type, handle, LISTENER_OPTIONS)
      );
    },
  };
};
